import { z } from "zod";
import type { AbilityScore } from "@/lib/core/schemas/ability";

declare module "@/lib/core/schemas/ability" {
  // biome-ignore lint/nursery/useConsistentTypeDefinitions: declaration-merge target requires interface
  interface AbilityScoreRegistry<_R extends string = "dnd5e"> {
    str: true;
    dex: true;
    con: true;
    int: true;
    wis: true;
    cha: true;
  }
}

/** The dnd5e ability-score vocabulary, resolved from the core registry.
 *  Resolves to "str" | "dex" | "con" | "int" | "wis" | "cha".
 *
 *  Sibling surface: SaveTag<R> (lib/kernel/) overlaps this set from the
 *  save-direction angle; consolidation is out of scope (spec §1, §12). */
export type Dnd5eAbilityScoreTag = AbilityScore<"dnd5e">;

/** Runtime validation surface — a dnd5e concern, NOT a core one. Hand-authored;
 *  kept in lockstep with the registry augmentation above by the AssertEqual
 *  parity guard below. */
export const Dnd5eAbilityScoreTagSchema = z.enum([
  "str",
  "dex",
  "con",
  "int",
  "wis",
  "cha",
] as const);

/** Ordered runtime list — replaces the ASI_VALID Sets and inline arrays. */
export const DND5E_ABILITY_SCORES: readonly Dnd5eAbilityScoreTag[] =
  Dnd5eAbilityScoreTagSchema.options;

// Drift guard (LAF-337 CreatureTypeRegistry pattern): asserts the hand-authored
// Zod enum equals the registry-augmented union, in BOTH directions. Adding a
// member to the registry without the enum (or vice versa) makes the unions
// unequal → AssertEqual resolves to `never` → `true` is not assignable → typecheck
// fails. NOT the weaker `T extends true` form.
type AssertEqual<A, B> = [A] extends [B]
  ? [B] extends [A]
    ? true
    : never
  : never;
export const _abilityScoreTagParity: AssertEqual<
  (typeof Dnd5eAbilityScoreTagSchema.options)[number],
  Dnd5eAbilityScoreTag
> = true;
